import React, { useEffect, useMemo, useState } from "react";
import { Row, Col, CardTitle, Card, CardBody, FormGroup, Label, Input, Button, CustomInput, Form, } from "reactstrap";
import Select from 'react-select';
import "react-datepicker/dist/react-datepicker.css";
import { Formik, useFormik } from 'formik';
import * as Yup from 'yup';
import { FiSave } from "react-icons/fi";
import nv2 from "../../../assets/images/brands/b-nav-2.svg";
import {
  createEmployee,
  fetchEmployeeTabWiseInfo
} from "store/actions";
import { useDispatch, useSelector } from "react-redux";

const EmployeeAccount = ({ empID, authDetails, isActive }) => {
  const dispatch = useDispatch();
  const {
    employeeAccountInfo = [],
    loading,
    error,
  } = useSelector((state) => ({
    employeeAccountInfo: empID == 0 ? [] : state.EmployeeDetailsReducer?.employeeTabData?.account || [],
    loading: state.employeeReducer?.loading,
    error: state.employeeReducer?.error,
  }));

  const numericOnly = /^[0-9]+$/;
  const alphaNumSpecial = /^[a-zA-Z0-9\s.,/#\-()]+$/;
  const ifscRegex = /^[A-Za-z0-9]{11}$/;

  const [empData, setEmpData] = useState([])

  const formik = useFormik({
    initialValues: {
      bankAccountNo: '',
      accHolderName: '',
      bankName: '',
      branch: '',
      ifscCode: '',
      esiNo: '',
      epfNo: '',
    },
    validationSchema: Yup.object({
      bankAccountNo: Yup.string()
    .required("Bank Account No is required")
    .max(20, "Bank Account No must not exceed 20 digits")
    .matches(numericOnly, "Only numbers allowed"),
 
      accHolderName: Yup.string()
        .matches(/^[a-zA-Z\s]+$/, 'Only alphabets and spaces are allowed')
        .min(3, 'Account Holder Name must be at least 3 characters')
        .max(40, 'Account Holder Name cannot exceed 40 characters')
        .required('Account Holder Name is required'),
 
      bankName: Yup.string()
        .matches(/^[a-zA-Z\s]+$/, 'Only alphabets and spaces are allowed')
        .min(3, 'Bank Name must be at least 3 characters')
        .max(40, 'Bank Name cannot exceed 40 characters')
        .required('Bank Name is required'),
 
      branch: Yup.string()
        .matches(/^[a-zA-Z0-9\s]+$/, 'Only alphanumeric characters and spaces are allowed')
        .min(3, 'Branch must be at least 3 characters')
        .max(40, 'Branch cannot exceed 40 characters')
        .required('Branch is required'),
 
      ifscCode: Yup.string()
    .required("IFSC Code is required")
    .length(11, "IFSC Code must be exactly 11 characters")
    .matches(ifscRegex, "Only alphabets and numbers allowed"),
 
      esiNo: Yup.string()
    .nullable()
    .max(25, "ESI No must not exceed 25 digits")
    .matches(numericOnly, "Only numbers allowed"),
 
  epfNo: Yup.string()
    .nullable()
    .max(25, "EPF No must not exceed 25 digits")
    .matches(numericOnly, "Only numbers allowed"),
    }),
    onSubmit: (values) => {
      const payload = {
        emp: [
          {
            emp_id: empID,
            bank_acno: values.bankAccountNo,
            acc_holder_name: values.accHolderName,
            bank_name: values.bankName,
            branch: values.branch,
            esi_no: values.esiNo,
            epf_no: values.epfNo,
            ifsc_code: values.ifscCode,
          }
        ]
      };
      const formData = new FormData();
      formData.append("lst_employee_personnel", JSON.stringify(payload));
      formData.append("create_by", authDetails?.user_id);
      dispatch(createEmployee(formData, 'account'));
      formik.resetForm()
    },
  });

  useEffect(() => {
    if (employeeAccountInfo && employeeAccountInfo.length > 0) {
      const empData = employeeAccountInfo[0]; // Assuming single employee data
      formik.setValues({
        bankAccountNo: empData.bank_acno || "",
        accHolderName: empData.acc_holder_name || "",
        bankName: empData.bank_name || "",
        branch: empData.branch || "",
        ifscCode: empData.ifsc_code || "",
        esiNo: empData.esi_no || "",
        epfNo: empData.epf_no || "",
      });
    }
  }, [employeeAccountInfo]);


  useEffect(() => {
    if (isActive) {
      dispatch(fetchEmployeeTabWiseInfo(empID, 'account'));
    }
  }, [dispatch, isActive]);

  return (
    <Form onSubmit={formik.handleSubmit}>
      <Card>
        <div className="card-main-title">
          <h6 className="title">
            <img src={nv2} className="img-fluid w-auto h-auto" alt="img" />
            Account Details
          </h6>
        </div>
        <CardBody>
          <Row>
            <Col md={4} className="mb-3">
              <FormGroup>
                <Label for="bankAccountNo">Bank A/C No <span className="text-danger">*</span></Label>
                <Input
                  type="text"
                  id="bankAccountNo"
                  name="bankAccountNo"
                  maxLength={20}
                  value={formik.values.bankAccountNo}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                {formik.touched.bankAccountNo && formik.errors.bankAccountNo ? (
                  <div className="text-danger">{formik.errors.bankAccountNo}</div>
                ) : null}
              </FormGroup>
            </Col>

            <Col md={4} className="mb-3">
              <FormGroup>
                <Label for="accHolderName">Acc Holder Name <span className="text-danger">*</span></Label>
                <Input
                  type="text"
                  id="accHolderName"
                  name="accHolderName"
                  maxLength={40}
                  value={formik.values.accHolderName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                {formik.touched.accHolderName && formik.errors.accHolderName ? (
                  <div className="text-danger">{formik.errors.accHolderName}</div>
                ) : null}
              </FormGroup>
            </Col>

            <Col md={4} className="mb-3">
              <FormGroup>
                <Label for="bankName">Bank Name <span className="text-danger">*</span></Label>
                <Input
                  type="text"
                  id="bankName"
                  name="bankName"
                  maxLength={40}
                  value={formik.values.bankName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                {formik.touched.bankName && formik.errors.bankName ? (
                  <div className="text-danger">{formik.errors.bankName}</div>
                ) : null}
              </FormGroup>
            </Col>

            <Col md={4} className="mb-3">
              <FormGroup>
                <Label for="branch">Branch <span className="text-danger">*</span></Label>
                <Input
                  type="text"
                  id="branch"
                  name="branch"
                  maxLength={40}
                  value={formik.values.branch}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                {formik.touched.branch && formik.errors.branch ? (
                  <div className="text-danger">{formik.errors.branch}</div>
                ) : null}
              </FormGroup>
            </Col>

            <Col md={4} className="mb-3">
              <FormGroup>
                <Label for="esiNo">ESI No</Label>
                <Input
                  type="text"
                  id="esiNo"
                  name="esiNo"
                  maxLength={25}
                  value={formik.values.esiNo}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                {formik.touched.esiNo && formik.errors.esiNo ? (
                  <div className="text-danger">{formik.errors.esiNo}</div>
                ) : null}
              </FormGroup>
            </Col>

            <Col md={4} className="mb-3">
              <FormGroup>
                <Label for="epfNo">EPF No</Label>
                <Input
                  type="text"
                  id="epfNo"
                  maxLength={25}
                  name="epfNo"
                  value={formik.values.epfNo}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                {formik.touched.epfNo && formik.errors.epfNo ? (
                  <div className="text-danger">{formik.errors.epfNo}</div>
                ) : null}
              </FormGroup>
            </Col>

            <Col md={4} className="mb-3">
              <FormGroup>
                <Label for="ifscCode">IFSC Code <span className="text-danger">*</span></Label>
                <Input
                  type="text"
                  id="ifscCode"
                  name="ifscCode"
                  maxLength={11}
                  value={formik.values.ifscCode}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                {formik.touched.ifscCode && formik.errors.ifscCode ? (
                  <div className="text-danger">{formik.errors.ifscCode}</div>
                ) : null}
              </FormGroup>
            </Col>
            <Col className="d-flex justify-content-end align-items-center">
              <Button type="submit" className="btn btn button-save d-flex align-items-center gap-2">
                <i className="fas fa-save " /> Save & Continue
              </Button>
            </Col>
          </Row>

        </CardBody>
      </Card>
    </Form>
  );
};

export default EmployeeAccount;
